import useAction from '@shared/hooks/useAction';
import { useSelector } from 'react-redux';
import { Reducer } from 'redux';

const initialState = {
  dismissed: false,
  forceTriggered: false,
};
type State = typeof initialState;

/**
 * The BeforeInstallPromptEvent is fired at the Window.onbeforeinstallprompt handler
 * before a user is prompted to "install" a web site to a home screen on mobile.
 */
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
interface BeforeInstallPromptEvent extends Event {
  /**
   * Returns an array of DOMString items containing the platforms on which the event was dispatched.
   * This is provided for user agents that want to present a choice of versions to the user such as,
   * for example, "web" or "play" which would allow the user to chose between a web version or
   * an Android version.
   */
  readonly platforms: string[];

  /**
   * Returns a Promise that resolves to a DOMString containing either "accepted" or "dismissed".
   */
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;

  /**
   * Allows a developer to show the install prompt at a time of their own choosing.
   * This method returns a Promise.
   */
  prompt(): Promise<void>;
}
declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }
}

let event = null as BeforeInstallPromptEvent | null;

export const getAppInstallEvent = () => event;
export const setAppInstallEvent = (e: BeforeInstallPromptEvent | null) => (event = e);

const APP_INSTALL_DISMISSED = 'APP_INSTALL_DISMISSED';
const APP_INSTALL_SET_FORCED = 'APP_INSTALL_SET_FORCED';

const actionCreators = {
  appInstallDismissed: () => ({ type: APP_INSTALL_DISMISSED }) as const,
  forceAppInstall: (data: boolean) => ({ type: APP_INSTALL_SET_FORCED, data }) as const,
};

type Action = ReturnType<(typeof actionCreators)[keyof typeof actionCreators]>;

const appInstall: Reducer<State, Action> = (state = initialState, action) => {
  switch (action.type) {
    case APP_INSTALL_DISMISSED:
      return { ...state, dismissed: true, forceTriggered: false };
    case APP_INSTALL_SET_FORCED:
      return { ...state, forceTriggered: action.data, dismissed: false };
    default:
      return state;
  }
};

export default appInstall;

const appInstallSelector = (state: { appInstall: State }) => state.appInstall;

export const useAppInstallState = () => useSelector(appInstallSelector);

export const useAppInstallActions = () => ({
  dismiss: useAction(actionCreators.appInstallDismissed),
  force: useAction(actionCreators.forceAppInstall),
});
